/**
 * ExportHistoryContext - Contexto global para historial de exportaciones
 * Permite que todas las ventanas flotantes compartan el mismo historial
 * También gestiona el estado de visibilidad del panel de historial
 * 
 * NOTA: Usa sessionStorage para datos temporales (se limpia al cerrar pestaña)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';

const STORAGE_KEY = 'exportHistory';
const MAX_ENTRIES = 50;

// Usar sessionStorage para datos temporales (más seguro que localStorage)
const storage = typeof window !== 'undefined' ? window.sessionStorage : null;

const ExportHistoryContext = createContext(null);

export const ExportHistoryProvider = ({ children }) => {
  const [history, setHistory] = useState([]);
  
  // Estado de visibilidad del panel - gestionado globalmente
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPanelMinimized, setIsPanelMinimized] = useState(false);

  // Cargar historial al iniciar
  useEffect(() => {
    try {
      const saved = storage?.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setHistory(Array.isArray(parsed) ? parsed : []);
      }
    } catch (e) {
      logger.error('Error loading export history:', e);
      setHistory([]);
    }
  }, []);

  // Añadir entrada al historial
  const addEntry = useCallback((entry) => {
    const newEntry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...entry
    };
    
    setHistory(prev => {
      const newHistory = [newEntry, ...prev.slice(0, MAX_ENTRIES - 1)];
      try {
        storage?.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (e) {
        logger.error('Error saving export history:', e);
      }
      return newHistory;
    });
    
    return newEntry;
  }, []);

  // Eliminar entrada
  const removeEntry = useCallback((id) => {
    setHistory(prev => {
      const newHistory = prev.filter(e => e.id !== id);
      storage?.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  // Limpiar historial
  const clearHistory = useCallback(() => {
    setHistory([]);
    storage?.removeItem(STORAGE_KEY);
  }, []);
  
  // Abrir panel de historial
  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
    setIsPanelMinimized(false);
  }, []);
  
  // Cerrar panel de historial
  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setIsPanelMinimized(false);
  }, []);
  
  // Minimizar/restaurar panel
  const toggleMinimize = useCallback(() => {
    setIsPanelMinimized(prev => !prev);
  }, []);
  
  // Memoizar el valor del contexto
  const value = useMemo(() => ({
    history,
    addEntry,
    removeEntry,
    clearHistory,
    // Estado del panel
    isPanelOpen,
    isPanelMinimized,
    openPanel,
    closePanel,
    toggleMinimize,
    setIsPanelMinimized,
  }), [history, addEntry, removeEntry, clearHistory, isPanelOpen, isPanelMinimized, openPanel, closePanel, toggleMinimize]);

  return (
    <ExportHistoryContext.Provider value={value}>
      {children}
    </ExportHistoryContext.Provider>
  );
};

// Hook para usar el contexto
export const useExportHistory = () => {
  const context = useContext(ExportHistoryContext);
  if (!context) {
    // Fallback si no está dentro del Provider - para compatibilidad
    logger.warn('useExportHistory debe usarse dentro de ExportHistoryProvider');
    return {
      history: [],
      addEntry: () => {},
      removeEntry: () => {},
      clearHistory: () => {},
      isPanelOpen: false,
      isPanelMinimized: false,
      openPanel: () => {},
      closePanel: () => {},
      toggleMinimize: () => {},
      setIsPanelMinimized: () => {},
    };
  }
  return context;
};

export default ExportHistoryContext;
